import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Gavel, Trash2, X } from 'lucide-react';
import { CommentBody, MentionTextarea, useMentions } from '@/components/ui/mentions';
import { api, errorMessage } from '@/lib/api';
import { useTracker } from '@/lib/tracker';
import {
  PARTIES,
  SEVERITIES,
  STATUS_LABEL,
  TYPE_LABEL,
  bandOf,
  partyLabel,
  raidKey,
  useOpenRaid,
  useRaidItem,
  type RaidDetail,
  type RaidItem,
  type RaidStatus,
} from '@/lib/raid';
import type { Program } from '@/lib/types';
import { fmtDate, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Badge, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';
import { useOpenTask } from '@/components/tracker/TaskPanel';
import { RaidRating, RaidStatusBadge } from './RaidTable';
import { AiBadge, useAssisted } from '@/components/ai/Assistant';

export function RaidPanelHost({ program }: { program: Program }) {
  const [params] = useSearchParams();
  const id = params.get('raid');
  return id ? <RaidPanel key={id} id={id} program={program} /> : null;
}

type Form = Partial<{
  title: string;
  description: string | null;
  status: RaidStatus;
  phaseId: string | null;
  ownerParty: RaidItem['ownerParty'];
  ownerId: string | null;
  targetDate: string | null;
  likelihood: number | null;
  impact: number | null;
  mitigation: string | null;
  severity: RaidItem['severity'];
  resolution: string | null;
}>;

function RaidPanel({ id, program }: { id: string; program: Program }) {
  const open = useOpenRaid();
  const openTask = useOpenTask();
  const close = () => open(null);
  const q = useRaidItem(program.id, id);
  const assisted = useAssisted(program, [id]);
  const tracker = useTracker(program.id);
  const qc = useQueryClient();
  const toast = useToast();
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<Element | null>(document.activeElement);
  const [form, setForm] = useState<Form>({});
  const mentions = useMentions();
  const base = `/api/programs/${program.id}/raid/${id}`;

  useEffect(() => {
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) =>
      e.key === 'Escape' && !(e.target as HTMLElement).closest('input,textarea,select') && close();
    document.addEventListener('keydown', onKey);
    const el = returnFocus.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      (el as HTMLElement | null)?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: raidKey(program.id) });
    void qc.invalidateQueries({ queryKey: ['tracker', program.id] });
  };
  const save = useMutation({
    mutationFn: (body: Form) => api.patch(base, body),
    onSuccess: () => {
      setForm({});
      refresh();
      toast.ok('Saved.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const addComment = useMutation({
    mutationFn: (body: string) => api.post(`${base}/comments`, { body }),
    onSuccess: () => {
      mentions.reset();
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(base),
    onSuccess: () => {
      refresh();
      close();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const i = q.data;
  const can = (f: string) => i?.canEdit === 'all' || (Array.isArray(i?.canEdit) && i.canEdit.includes(f));
  const v = <K extends keyof Form>(k: K): Form[K] => (k in form ? form[k] : (i?.[k as keyof RaidDetail] as Form[K]));
  const set = (patch: Form) => setForm((f) => ({ ...f, ...patch }));
  const dirty = Object.keys(form).length > 0;
  const data = tracker.data;
  const score = (v('likelihood') ?? 0) * (v('impact') ?? 0);

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="RAID item">
      <div className="absolute inset-0 bg-black/20" onClick={close} />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-[min(640px,100vw)] flex-col border-l border-border bg-surface shadow-2xl outline-none"
      >
        <div className="flex items-center gap-2 border-b border-border px-5 py-3">
          {i && (
            <>
              <span className="font-mono text-xs text-muted">{i.ref}</span>
              <Badge>{TYPE_LABEL[i.type]}</Badge>
              <RaidStatusBadge s={i.status} />
              <RaidRating i={i} />
              <AiBadge runId={assisted.data?.[i.id]} />
            </>
          )}
          <div className="ml-auto flex items-center gap-1">
            {i?.canEdit === 'all' && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Delete item"
                onClick={() =>
                  window.confirm(
                    'Delete this RAID item? It moves to Recently deleted in Admin, where it can be restored for 30 days.',
                  ) && remove.mutate()
                }
              >
                <Trash2 />
              </Button>
            )}
            <Button variant="ghost" size="icon" aria-label="Close" onClick={close}>
              <X />
            </Button>
          </div>
        </div>
        {!i ? (
          <Spinner className="h-64" />
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
            {can('title') ? (
              <Input
                value={v('title') ?? ''}
                onChange={(e) => set({ title: e.target.value })}
                className="text-base font-semibold"
                aria-label="Title"
              />
            ) : (
              <h2 className="text-base font-semibold">{i.title}</h2>
            )}
            <p className="text-xs text-muted">
              Raised {fmtDate(i.raisedAt)}
              {i.raisedBy ? ` by ${i.raisedBy}` : ''}
              {i.sourceRef ? ` · ${i.sourceRef}` : ''}
            </p>

            <div className="grid grid-cols-2 gap-3">
              <Row label="Status">
                {can('status') ? (
                  <Select
                    value={v('status')}
                    onChange={(e) => set({ status: e.target.value as RaidStatus })}
                    aria-label="Status"
                  >
                    {(Object.keys(STATUS_LABEL) as RaidStatus[]).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </Select>
                ) : (
                  STATUS_LABEL[i.status]
                )}
              </Row>
              <Row label="Phase">
                {can('phaseId') ? (
                  <Select
                    value={v('phaseId') ?? ''}
                    onChange={(e) => set({ phaseId: e.target.value || null })}
                    aria-label="Phase"
                  >
                    <option value="">Program-wide</option>
                    {data?.phases.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.number}. {p.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  (data?.phases.find((p) => p.id === i.phaseId)?.name ?? 'Program-wide')
                )}
              </Row>
              <Row label="Owner">
                {can('ownerParty') ? (
                  <div className="flex gap-2">
                    <Select
                      value={v('ownerParty')}
                      onChange={(e) => set({ ownerParty: e.target.value as RaidItem['ownerParty'] })}
                      aria-label="Owner party"
                    >
                      {PARTIES.map((p) => (
                        <option key={p} value={p}>
                          {partyLabel(p, program)}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={v('ownerId') ?? ''}
                      onChange={(e) => set({ ownerId: e.target.value || null })}
                      aria-label="Owner"
                    >
                      <option value="">No person</option>
                      {data?.members.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  (i.ownerName ?? partyLabel(i.ownerParty, program))
                )}
              </Row>
              <Row label="Target date">
                {can('targetDate') ? (
                  <Input
                    type="date"
                    value={v('targetDate') ?? ''}
                    onChange={(e) => set({ targetDate: e.target.value || null })}
                    aria-label="Target date"
                  />
                ) : i.targetDate ? (
                  fmtDate(i.targetDate)
                ) : (
                  'None'
                )}
              </Row>
              {i.type === 'RISK' && (
                <Row label={`Likelihood x impact${score ? ` = ${score} (${bandOf(score).toLowerCase()})` : ''}`}>
                  {can('likelihood') ? (
                    <div className="flex gap-2">
                      {(['likelihood', 'impact'] as const).map((k) => (
                        <Select
                          key={k}
                          value={v(k) ?? ''}
                          onChange={(e) => set({ [k]: Number(e.target.value) })}
                          aria-label={k === 'likelihood' ? 'Likelihood' : 'Impact'}
                        >
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </Select>
                      ))}
                    </div>
                  ) : (
                    `${i.likelihood} x ${i.impact}`
                  )}
                </Row>
              )}
              {i.type === 'ISSUE' && (
                <Row label="Severity">
                  {can('severity') ? (
                    <Select
                      value={v('severity') ?? ''}
                      onChange={(e) => set({ severity: e.target.value as RaidItem['severity'] })}
                      aria-label="Severity"
                    >
                      {SEVERITIES.map((s) => (
                        <option key={s} value={s}>
                          {s.toLowerCase()}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    i.severity?.toLowerCase()
                  )}
                </Row>
              )}
            </div>

            <TextBlock
              label="Description"
              value={v('description')}
              edit={can('description')}
              onChange={(x) => set({ description: x })}
            />
            {(i.type === 'RISK' || i.type === 'ISSUE' || i.type === 'DEPENDENCY') && (
              <TextBlock
                label={i.type === 'RISK' ? 'Mitigation' : 'Action plan'}
                value={v('mitigation')}
                edit={can('mitigation')}
                onChange={(x) => set({ mitigation: x })}
              />
            )}
            {i.type !== 'DECISION' && (
              <TextBlock
                label="Resolution"
                value={v('resolution')}
                edit={can('resolution')}
                onChange={(x) => set({ resolution: x })}
              />
            )}

            {dirty && (
              <div className="flex gap-2">
                <Button onClick={() => save.mutate(form)} loading={save.isPending}>
                  Save changes
                </Button>
                <Button variant="ghost" onClick={() => setForm({})}>
                  Discard
                </Button>
              </div>
            )}

            {i.type === 'DECISION' && <Decision program={program} i={i} onDone={refresh} />}

            {(i.taskIds.length > 0 || i.deliverableIds.length > 0) && (
              <section className="space-y-1.5">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted">Linked</h3>
                {i.taskIds.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => openTask(t)}
                    className="block text-left text-sm text-brand-text hover:underline"
                  >
                    Task: {data?.tasks.find((x) => x.id === t)?.title ?? 'Task'}
                  </button>
                ))}
                {i.deliverableIds.map((d) => (
                  <Link
                    key={d}
                    to={`/p/${program.id}/deliverables/${d}`}
                    className="block text-sm text-brand-text hover:underline"
                  >
                    Deliverable: {data?.deliverables.find((x) => x.id === d)?.name ?? 'Deliverable'}
                  </Link>
                ))}
              </section>
            )}

            <section className="space-y-2">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted">Comments</h3>
              {i.commentList.map((c) => (
                <div key={c.id} className="rounded-md bg-surface-2/60 px-3 py-2 text-sm">
                  <p className="text-xs text-muted">
                    {c.author.name} · {fmtDateTime(c.createdAt)}
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap">
                    <CommentBody body={c.body} markdown={false} />
                  </p>
                </div>
              ))}
              <MentionTextarea
                program={program}
                m={mentions}
                rows={2}
                placeholder="Add a comment"
                aria-label="Comment"
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={!mentions.text.trim()}
                loading={addComment.isPending}
                onClick={() => addComment.mutate(mentions.body())}
              >
                Comment
              </Button>
            </section>

            <section className="space-y-1.5">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted">History</h3>
              <ul className="space-y-1 text-xs text-muted">
                {[...i.history].reverse().map((h, n) => (
                  <li key={n}>
                    {fmtDateTime(h.at)} · {h.actor} ·{' '}
                    {h.action === 'create'
                      ? 'raised'
                      : h.changes.length
                        ? h.changes.map((c) => c.field).join(', ')
                        : h.action.replace(/_/g, ' ')}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function TextBlock({
  label,
  value,
  edit,
  onChange,
}: {
  label: string;
  value: string | null | undefined;
  edit: boolean;
  onChange: (v: string | null) => void;
}) {
  if (!edit && !value) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted">{label}</p>
      {edit ? (
        <Textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} aria-label={label} />
      ) : (
        <p className="whitespace-pre-wrap text-sm">{value}</p>
      )}
    </div>
  );
}

function Decision({ program, i, onDone }: { program: Program; i: RaidDetail; onDone: () => void }) {
  const toast = useToast();
  const [choice, setChoice] = useState(i.options?.[0]?.label ?? '');
  const [decidedBy, setDecidedBy] = useState('Steering Committee');
  const [rationale, setRationale] = useState('');
  const decide = useMutation({
    mutationFn: () => api.post(`/api/programs/${program.id}/raid/${i.id}/decision`, { choice, decidedBy, rationale }),
    onSuccess: () => {
      toast.ok('Decision recorded.');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <section className="space-y-3 rounded-lg border border-border p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <Gavel className="size-4" /> Decision
      </h3>
      {i.options && i.options.length > 0 && (
        <ul className="space-y-1 text-sm">
          {i.options.map((o) => (
            <li key={o.label}>
              <span className={o.label === i.choice ? 'font-semibold' : ''}>{o.label}</span>
              {o.notes && <span className="text-muted">: {o.notes}</span>}
            </li>
          ))}
        </ul>
      )}
      {i.choice ? (
        <div className="text-sm">
          <p>
            <span className="font-medium">{i.choice}</span>, decided by {i.decidedBy}
            {i.decidedAt ? ` on ${fmtDate(i.decidedAt)}` : ''}.
          </p>
          {i.rationale && <p className="mt-1 text-muted">{i.rationale}</p>}
        </div>
      ) : i.canDecide ? (
        <div className="space-y-3">
          <Field label="Choice" htmlFor="choice">
            {i.options?.length ? (
              <Select id="choice" value={choice} onChange={(e) => setChoice(e.target.value)}>
                {i.options.map((o) => (
                  <option key={o.label} value={o.label}>
                    {o.label}
                  </option>
                ))}
              </Select>
            ) : (
              <Input id="choice" value={choice} onChange={(e) => setChoice(e.target.value)} />
            )}
          </Field>
          <Field label="Decided by" htmlFor="decided-by">
            <Input id="decided-by" value={decidedBy} onChange={(e) => setDecidedBy(e.target.value)} />
          </Field>
          <Field label="Rationale" htmlFor="rationale" hint="Recorded with the decision.">
            <Textarea id="rationale" rows={2} value={rationale} onChange={(e) => setRationale(e.target.value)} />
          </Field>
          <Button
            onClick={() => decide.mutate()}
            loading={decide.isPending}
            disabled={!choice.trim() || !decidedBy.trim() || !rationale.trim()}
          >
            <Gavel /> Record decision
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted">
          Awaiting the committee{i.targetDate ? `, needed by ${fmtDate(i.targetDate)}` : ''}.
        </p>
      )}
    </section>
  );
}
